/**
 * Which FORM a document form's field belongs to, and how to say so.
 *
 * §6g: everything that reads or writes a value names its place as
 * `TXN.FORM.COLUMN`, so a field is never loose — it belongs to one form of one
 * covered transaction, and that is what the Fields tab groups by. The rules
 * here mirror `er/paths.py` and `er/execution.py`; nothing is inferred from a
 * name, and a field whose map names a form this designer cannot see still gets
 * a group of its own rather than being quietly dropped from the list.
 *
 * Pure — no React, no network.
 */

import type { SourceColumn, SourceTxn } from './sources'
import type { FieldRow, TxnRow } from './types'
import { destinationIn, parsePath } from './destination'

// The path grammar lives with the destination rules now. Re-exported so every
// module that already reads it from here is unchanged.
export { parsePath }

/** How a field gets its value, as one choice out of six. */
export type SourceKind =
  'entry' | 'context' | 'rule' | 'calc' | 'repeat' | 'sign'

/** The group id for fields the document works out or signs for itself. */
export const OWN_GROUP = '@own'

/**
 * The group for values the document KEEPS rather than writes to a transaction.
 *
 * Its own group because the alternative was the defect this closes: a field the
 * engine routes to the generic capture object was listed under the primary's
 * form 1, beside the columns that really are the transaction's, with nothing
 * to tell the two apart. An author looking for where their agreement date was
 * stored read that as "it goes to the artist master", and it does not.
 */
export const DOC_DATA_GROUP = '@document-data'

/**
 * The group for values that identify the DOCUMENT rather than sit in a row.
 *
 * A form built over a visual is one slice of one run, and the run was bounded
 * by criteria answered at the prompt. They map to the engine's `KEY.<NAME>`
 * namespace, which is not a transaction — so without a group of their own they
 * landed in an orphan headed "Form STATE", which is neither true nor findable.
 */
export const CRITERIA_GROUP = '@criteria'

/** The engine's namespace for the document's identity — `er/paths.py`. */
export function isKeyPath(path: string): boolean {
  const parts = String(path || '').trim().split('.')
  if (parts.length !== 2 || !parts[1]) return false
  const head = parts[0].trim().toUpperCase()
  return head === 'KEY' || head === 'KEYS'
}

export interface FieldEntry {
  field: FieldRow
  /** Position in `template.fields` — what every mutation is keyed by. */
  index: number
}

export interface FieldGroup {
  /** `ENGAGEMENT.2`, or `@own`. */
  id: string
  /** The form's OWN heading, as its transaction names it. */
  heading: string
  /** `FORM 2` — shown as a badge inside the group, never as its name. */
  formBadge: string
  formNo: string
  txnTitle: string
  txnIdent: string
  /** True for the first group of each transaction — the rail draws a header. */
  firstOfTxn: boolean
  detail: boolean
  /**
   * A MANY source's header form — `SourceForm.setHeader`.
   *
   * `detail` and this are both true on a visual's result, and the difference
   * is what a field on it MEANS: not "one per row of a child table" but "the
   * document's own value, read off its first row". Without it the group read
   * "where each row gets its value" over a heading.
   */
  setHeader: boolean
  readOnly: boolean
  own: boolean
  /** True when the transaction's model has not arrived (or would not load). */
  unresolved: boolean
  table: string
  columns: SourceColumn[]
  /** The title of the repeat field that loops this form, when one does. */
  repeatTitle: string
  entries: FieldEntry[]
}

/** How this field gets its value. `kind` decides first; the binding settles a merge. */
export function sourceOf(field: FieldRow): SourceKind {
  if (field.kind === 'repeat') return 'repeat'
  if (field.kind === 'signature') return 'sign'
  if (field.kind === 'calc') return 'calc'
  if (field.kind === 'merge') {
    const mode = String((field.binding as { mode?: unknown } | null)?.mode || '')
      .trim().toLowerCase()
    // A merge with no binding is not "the operator fills it": it DISPLAYS the
    // value its `map` path already carries on the instance tree
    // (`execution._seed_from_tree`), which is the same thing a `context`
    // binding does for the anchor. One source kind covers both.
    return mode === 'rule' ? 'rule' : 'context'
  }
  return 'entry'
}

/**
 * The group a field belongs to.
 *
 * An empty `map` is not "nowhere": §6g says it keeps the original rule — the
 * PRIMARY transaction's header column of the same key — so an unmapped entry
 * field belongs to the primary's form 1, which is where the author will look
 * for it. A repeat names the form it LOOPS, but it is placed at header level,
 * so it groups with that transaction's form 1 and its cells (which carry
 * `in_table`) group with the detail form their own map names.
 */
export function groupIdOf(
  field: FieldRow, primaryIdent: string,
  fields: FieldRow[] = [], txns: TxnRow[] = [], catalog: SourceTxn[] = [],
): string {
  const kind = sourceOf(field)
  if (kind === 'repeat') {
    const [txn] = parsePath(field.path)
    return `${txn || primaryIdent}.1`
  }
  if (isKeyPath(field.map || '')) return CRITERIA_GROUP
  const [txn, form] = parsePath(field.map)
  if (txn) return `${txn}.${form || '1'}`
  // No map. Whether this lands on the primary's column of the same name or in
  // document data is the engine's inference, and this is the one place the
  // author can see which. Asked only when there is something to ask it with —
  // the older two-argument call still groups the old way.
  if (kind === 'entry' && (fields.length || catalog.length)) {
    const dest = destinationIn(field, fields, txns, catalog)
    if (dest.kind === 'document-data') return DOC_DATA_GROUP
  }
  if (kind === 'calc' || kind === 'sign') return OWN_GROUP
  return `${primaryIdent}.1`
}

/** The PATH segment a transaction is addressed by — its alias, else its name. */
export function identOf(txn: TxnRow): string {
  return String(txn.alias || txn.obj_name || '').trim().toUpperCase()
}

function ownGroup(entries: FieldEntry[]): FieldGroup {
  return {
    id: OWN_GROUP,
    heading: "The document's own",
    formBadge: '', formNo: '',
    txnTitle: "The document's own",
    txnIdent: '',
    firstOfTxn: true,
    detail: false, setHeader: false, readOnly: false, own: true,
    unresolved: false,
    table: '', columns: [], repeatTitle: '',
    entries,
  }
}

function criteriaGroup(entries: FieldEntry[]): FieldGroup {
  return {
    id: CRITERIA_GROUP,
    heading: 'Criteria of the run',
    formBadge: '', formNo: '',
    txnTitle: 'Criteria of the run',
    txnIdent: '',
    firstOfTxn: true,
    // Read-only by what it IS: a criterion was answered before the document
    // existed, and there is nothing behind it to write to.
    detail: false, setHeader: false, readOnly: true, own: true,
    unresolved: false,
    table: '', columns: [], repeatTitle: '',
    entries,
  }
}

function docDataGroup(entries: FieldEntry[]): FieldGroup {
  return {
    id: DOC_DATA_GROUP,
    // The Body tab's rail calls this "Filled in on this form"; one idea gets
    // one name across the designer, or an author learns two vocabularies for
    // the same set of fields.
    heading: 'Filled in on this form',
    formBadge: '', formNo: '',
    txnTitle: 'Filled in on this form',
    txnIdent: '',
    firstOfTxn: true,
    detail: false, setHeader: false, readOnly: false, own: true,
    unresolved: false,
    table: '', columns: [], repeatTitle: '',
    entries,
  }
}

/**
 * Every field, under the form that owns it.
 *
 * Groups come out in the order the document declares its transactions, forms
 * within a transaction in model order, then anything whose map names a form
 * this designer cannot see, then the document's own. A form with no fields is
 * kept when it is a transaction's FIRST form — that is the one somebody adds
 * to — and dropped otherwise, so a transaction with nine unused detail forms
 * does not bury the two that are used.
 */
export function buildFieldGroups(
  fields: FieldRow[], txns: TxnRow[], catalog: SourceTxn[],
): FieldGroup[] {
  const primary = (txns || []).find((t) => t.role === 'primary') || txns?.[0]
  const primaryIdent = primary ? identOf(primary) : ''

  const byId = new Map<string, FieldEntry[]>()
  const own: FieldEntry[] = []
  const docData: FieldEntry[] = []
  const criteria: FieldEntry[] = []
  ;(fields || []).forEach((field, index) => {
    const id = groupIdOf(field, primaryIdent, fields || [], txns || [],
      catalog || [])
    if (id === CRITERIA_GROUP) { criteria.push({ field, index }); return }
    if (id === DOC_DATA_GROUP) { docData.push({ field, index }); return }
    if (id === OWN_GROUP) { own.push({ field, index }); return }
    const list = byId.get(id)
    if (list) list.push({ field, index })
    else byId.set(id, [{ field, index }])
  })

  // The repeat fields, so a detail form can say which table it feeds.
  const repeatTitles = new Map<string, string>()
  ;(fields || []).forEach((field) => {
    if (sourceOf(field) !== 'repeat') return
    const [txn, form] = parsePath(field.path)
    if (!txn || !form) return
    repeatTitles.set(`${txn}.${form}`, field.title || field.key)
  })

  const out: FieldGroup[] = []
  const seen = new Set<string>()

  ;(txns || []).forEach((txn) => {
    const ident = identOf(txn)
    if (!ident) return
    const entry = (catalog || []).find((c) => c.ident === ident)
    const title = entry?.title || String(txn.obj_name || ident).replace(/_/g, ' ')
    const forms = entry?.forms || []
    // A transaction whose model has not arrived still gets its form 1, so the
    // fields mapped to it have somewhere to live and the author can see it is
    // covered.
    const shapes = forms.length
      ? forms.map((f) => ({
        formNo: f.formNo, heading: f.title, detail: f.detail,
        setHeader: !!f.setHeader,
        table: f.table, columns: f.columns, unresolved: false,
      }))
      : [{ formNo: '1', heading: 'Form 1', detail: false, setHeader: false,
        table: '', columns: [] as SourceColumn[], unresolved: true }]

    let first = true
    shapes.forEach((shape) => {
      const id = `${ident}.${shape.formNo}`
      const entries = byId.get(id) || []
      // Keep an empty form only when it is the transaction's first: that is
      // the one an author adds to, and the rest would be noise.
      if (!entries.length && shape.formNo !== shapes[0].formNo) return
      seen.add(id)
      out.push({
        id,
        heading: shape.heading || `Form ${shape.formNo}`,
        formBadge: `FORM ${shape.formNo}`,
        formNo: shape.formNo,
        txnTitle: title,
        txnIdent: ident,
        firstOfTxn: first,
        detail: shape.detail,
        setHeader: shape.setHeader,
        readOnly: !!txn.read_only,
        own: false,
        unresolved: shape.unresolved,
        table: shape.table,
        columns: shape.columns,
        repeatTitle: repeatTitles.get(id) || '',
        entries,
      })
      first = false
    })
  })

  // A map naming a form nothing covers. Never hidden — a field the author
  // cannot see is a field they cannot fix.
  const orphans = Array.from(byId.keys()).filter((id) => !seen.has(id)).sort()
  orphans.forEach((id) => {
    const [ident, formNo] = id.split('.')
    out.push({
      id,
      heading: `Form ${formNo || '1'}`,
      formBadge: `FORM ${formNo || '1'}`,
      formNo: formNo || '1',
      // No ident at all means the document covers no transaction yet: the
      // fields are real, they simply have nowhere to be saved.
      txnTitle: ident || 'Not saved to any transaction yet',
      txnIdent: ident,
      firstOfTxn: true,
      detail: formNo !== '1',
      setHeader: false,
      readOnly: false,
      own: false,
      unresolved: true,
      table: '', columns: [], repeatTitle: '',
      entries: byId.get(id) || [],
    })
  })

  if (criteria.length) out.push(criteriaGroup(criteria))
  if (docData.length) out.push(docDataGroup(docData))
  if (own.length) out.push(ownGroup(own))
  return out
}

/** The one-line sentence a row reads, split so the nouns can be emphasised. */
export function describeSource(
  field: FieldRow, group: FieldGroup,
): [string, string, string] {
  switch (sourceOf(field)) {
    case 'entry': {
      // A set header is NOT "every row": it is the document's own level, read
      // off its first row. Deciding this once here keeps the row, the group
      // heading and the placement panel saying one thing.
      const perRow = group.detail && !group.setHeader
      if (group.readOnly) {
        const [, , col] = parsePath(field.map)
        return ['From', group.txnTitle,
          `— ${columnHeading(group, col) || field.title || field.key}`]
      }
      if (field.protect) {
        // Locked, not absent. A row that read "typed in by the person filling
        // the form" over a field nobody can type into is the list disagreeing
        // with the page.
        return perRow
          ? ['Shown on', 'every row', '— locked']
          : ['Shown to the person filling the form', '', '— locked']
      }
      return perRow
        ? ['Typed in on', 'every row', 'of this table']
        : ['Typed in by the person filling the form', '', '']
    }
    case 'context': {
      if (group.id === CRITERIA_GROUP) {
        const [, name] = parsePath(field.map)
        return ['Asked at the prompt —', (name || field.key).toLowerCase(),
          '· the same on every page']
      }
      const [, , column] = parsePath(field.map)
      const named = columnHeading(group, column) || field.title || field.key
      return group.detail && !group.setHeader
        ? ['Comes with', 'the row', `— ${named}`]
        : ['From', group.txnTitle, `— ${named}`]
    }
    case 'rule': {
      const binding = (field.binding || {}) as Record<string, unknown>
      const source = String(binding.source_object || '').trim()
      const match = String(binding.match_column || '').trim()
      if (!source) return ['Looked up', '', '— no object chosen yet']
      return ['Looked up in', source.replace(/_/g, ' ').toLowerCase(),
        match ? `matched on ${match.replace(/_/g, ' ').toLowerCase()}` : '']
    }
    case 'calc':
      return field.calc
        ? ['Worked out —', field.calc, '']
        : ['Worked out', '', '— no formula yet']
    case 'repeat': {
      const [txn, form] = parsePath(field.path)
      if (!txn) return ['A repeating table', '', '— no form chosen yet']
      return ['One row for each', `${txn} form ${form || '1'}`, 'row']
    }
    default:
      return ['Signed', 'on the document', '']
  }
}

/** A column's own heading on its form, when the model is loaded. */
export function columnHeading(group: FieldGroup, dbName: string): string {
  if (!dbName) return ''
  const col = group.columns.find((c) => c.dbName === dbName.toUpperCase())
  return col ? col.heading : dbName
}

/** What a field HOLDS, in the words a business reader uses. */
export const TYPE_WORDS: Record<string, string> = {
  VARCHAR: 'Text', NUMERIC: 'Number', DATE: 'Date', BOOLEAN: 'Yes / no',
  USER: 'Person', IMAGE: 'Image', FILE: 'File', JSON: 'Structured',
}
